import {
  getCommitChannel,
  parseRetiredGeneration,
  subscribeToCommitChannel,
} from 'modules/viewer/workers/workerSelfRetirement';

// VX-4385: how long a freshly-bumped generation waits for the prior generation to confirm it has
// drained before it gives up and proceeds anyway. Active retirement drains the old worker
// synchronously the moment it receives the commit broadcast, so the ack normally arrives within a few
// frames; this deadline is only the safety bound so a missing/already-gone worker (or an environment
// without BroadcastChannel) can never wedge recovery. Kept short so the post-bump delivery gap stays
// small.
export const PRIOR_GENERATION_DRAIN_DEADLINE_MS = 2000;

// VX-4385: the retirement ack races the client. The retiring worker drains and acks the instant it
// receives the commit broadcast — which happens BEFORE the client can subscribe, because the client
// only reacts after the commit propagates through the generation atom, re-renders, sets the drain
// gate, and re-renders again. A listener created per-await (the old createChannel approach) would
// therefore usually miss the ack and always burn the full deadline.
//
// So instead one listener is installed at module load (long before any bump) on the SAME commit
// channel the worker acks on, and it simply remembers the highest generation that has acked
// retirement. A caller resolves immediately once that memory shows its prior generation has drained
// (the common case), or registers a waiter that the listener settles when a qualifying ack later
// arrives, with the bounded deadline as the safety net. Reuses workerSelfRetirement's channel rather
// than opening a duplicate.
interface DrainWaiter {
  priorGeneration: number;
  resolve: () => void;
  timer: ReturnType<typeof setTimeout>;
}

// Null only where BroadcastChannel is unavailable (SSR / unsupported env); there we never block.
const channel = getCommitChannel();
let highestRetiredGeneration = -1;
const pendingWaiters = new Set<DrainWaiter>();

const settleWaiter = (waiter: DrainWaiter): void => {
  // delete() returns false if already settled (deadline and ack can both fire) — idempotent.
  if (!pendingWaiters.delete(waiter)) return;
  clearTimeout(waiter.timer);
  waiter.resolve();
};

// Record the highest acked generation, then settle every waiter whose prior generation is now known
// to have drained. Shared by the always-on listener and the test seam below.
const recordRetiredGeneration = (retired: number): void => {
  if (retired > highestRetiredGeneration) highestRetiredGeneration = retired;
  // settleWaiter only ever deletes the waiter being visited, which Set.forEach tolerates.
  pendingWaiters.forEach((waiter) => {
    if (waiter.priorGeneration <= highestRetiredGeneration) settleWaiter(waiter);
  });
};

// The always-on listener. Shares the single main-thread escape-hatch channel instance with the
// coordination hook's presence/votes (VX-4391) — commit and coordination messages parse to null here
// and are ignored.
subscribeToCommitChannel((data) => {
  const retired = parseRetiredGeneration(data);
  if (retired != null) recordRetiredGeneration(retired);
});

/**
 * VX-4385: resolves once the generation immediately prior to a bump has confirmed it drained — i.e. a
 * `{ retiredGeneration }` ack for a generation >= `priorGeneration` has been seen on the commit
 * channel — or once `deadlineMs` elapses, whichever comes first. Callers gate the creation of the
 * new-generation worker (and therefore its heavy decode) on this, so the old and new OpenJPH WASM
 * heaps never coexist.
 *
 * Resolves immediately when the ack was already captured before this call (the common case, see the
 * module comment) and when no channel is available (so recovery is never wedged).
 */
export function awaitPriorGenerationDrained(
  priorGeneration: number,
  options?: { deadlineMs?: number }
): Promise<void> {
  if (channel == null) return Promise.resolve();
  if (highestRetiredGeneration >= priorGeneration) return Promise.resolve();

  const deadlineMs = options?.deadlineMs ?? PRIOR_GENERATION_DRAIN_DEADLINE_MS;
  return new Promise<void>((resolve) => {
    const waiter: DrainWaiter = {
      priorGeneration,
      resolve,
      // The callback only runs after this object is fully constructed, so closing over `waiter` is safe.
      timer: setTimeout(() => settleWaiter(waiter), deadlineMs),
    };
    pendingWaiters.add(waiter);
  });
}

// Test-only: reset the module-level retirement memory and drop any pending waiters so each test starts
// from a clean slate. The listener itself is installed once at import and intentionally persists.
export function resetGenerationDrainTrackingForTests(): void {
  highestRetiredGeneration = -1;
  pendingWaiters.forEach((waiter) => settleWaiter(waiter));
}

// Test-only: drive the exact path the always-on listener runs when the retiring worker acks. Used
// instead of posting on the shared BroadcastChannel mock, which the test harness bridges to the
// broadcast-channel library and so cannot carry a raw ack payload without tripping its envelope parser.
export function simulateRetirementAckForTests(retiredGeneration: number): void {
  recordRetiredGeneration(retiredGeneration);
}
